/**
 * CareLogix™ ORACLE — AI Agent Business Command™ (Phase 43, member surface).
 * Positioning: AI-enabled business services with human quality control.
 */
import React, { useCallback, useEffect, useState } from "react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/v2/oracle/ai-business`;

const C = {
  bg:"#0a0f0a", panel:"rgba(13,26,15,0.85)", ivory:"#f5f5dc", ivoryD:"#d4d4aa",
  gold:"#facc15", emerald:"#7AC860", amber:"#fcd34d", red:"#ef4444",
  border:"rgba(245,200,66,0.30)",
};
const S = {
  wrap:{background:C.bg, minHeight:"100vh", color:C.ivory, padding:"28px 22px", fontFamily:"'Inter', system-ui, sans-serif"},
  kicker:{color:C.gold, fontSize:11, letterSpacing:"0.22em", fontWeight:800, textTransform:"uppercase"},
  title:{fontFamily:"'Cinzel', serif", color:C.gold, fontSize:28, margin:"6px 0 6px"},
  sub:{color:C.ivoryD, fontSize:15, lineHeight:1.55, margin:"0 0 20px", maxWidth:820},
  tabs:{display:"flex", gap:8, flexWrap:"wrap", marginBottom:16, borderBottom:`1px solid ${C.border}`, paddingBottom:6},
  tabBtn:(active)=>({padding:"8px 14px", borderRadius:8, cursor:"pointer",
    background:active?"rgba(245,200,66,0.15)":"transparent",
    color:active?C.gold:C.ivory, border:`1px solid ${active?C.gold:C.border}`,
    fontFamily:"'Space Mono', monospace", fontSize:12, fontWeight:700, letterSpacing:"0.06em"}),
  grid:{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))", gap:14},
  card:{background:C.panel, border:`1px solid ${C.border}`, borderRadius:14, padding:16},
  ct:{fontFamily:"'Cinzel', serif", color:C.gold, fontSize:15, margin:"0 0 8px"},
  cs:{color:C.ivoryD, fontSize:12.5, lineHeight:1.55, margin:0},
  kv:{display:"flex", justifyContent:"space-between", padding:"3px 0", fontSize:12, color:C.ivoryD},
  input:{width:"100%", padding:"10px 12px", borderRadius:8, background:"rgba(0,0,0,0.35)", color:C.ivory, border:`1px solid ${C.border}`, fontSize:14, marginTop:4},
  btn:{background:C.gold, color:"#0a0a0a", border:"none", padding:"10px 18px", borderRadius:8, fontWeight:700, cursor:"pointer", fontSize:13, minHeight:48},
  err:{color:C.red, fontSize:12, marginTop:8},
  disc:{color:C.ivoryD, fontSize:11.5, marginTop:22, opacity:0.85, lineHeight:1.55, borderTop:`1px solid ${C.border}`, paddingTop:12},
};

async function apiGet(path){ const r=await fetch(`${API_BASE}${path}`); if(!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }
async function apiPost(path,body){ const r=await fetch(`${API_BASE}${path}`,{method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(body)}); if(!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }

export default function OracleAIBusinessPage(){
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [catalog, setCatalog]   = useState([]);
  const [projects, setProjects] = useState([]);
  const [pricing, setPricing]   = useState(null);
  const [rev, setRev]           = useState(null);
  const [err, setErr]           = useState(null);
  const [priceForm, setPriceForm] = useState({ ai_tool_cost:25, human_review_hours:3, hourly_value:150, delivery_complexity:"standard", client_urgency:"standard", compliance_risk:"LOW", revision_allowance:2 });

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [ov, cat, pj, rv] = await Promise.all([
        apiGet(""),
        apiGet("/service-catalog"),
        apiGet("/projects"),
        apiGet("/revenue-dashboard"),
      ]);
      setOverview(ov); setCatalog(cat.services||[]); setProjects(pj.projects||[]); setRev(rv);
    } catch(e){ setErr(String(e.message||e)); }
  },[]);
  useEffect(() => { load(); }, [load]);

  async function calcPrice(){
    try{ setPricing(await apiPost("/pricing/calculate", priceForm)); }catch(e){ setErr(String(e.message||e)); }
  }

  return (
    <main style={S.wrap} data-testid="oracle-ai-business-page">
      <div style={S.kicker}>ORACLE · AI AGENT BUSINESS COMMAND™</div>
      <h1 style={S.title}>AI-enabled business services, with human quality control.</h1>
      <p style={S.sub}>
        Identify legitimate service opportunities · price them responsibly · manage delivery ·
        track margins · enforce human review · preserve immutable EvidenceOS proofs.
        <strong style={{color:C.ivory}}> This is not raw AI resale.</strong> Every deliverable is
        reviewed by a human before it leaves the platform.
      </p>

      <nav style={S.tabs} aria-label="AI Business tabs" data-testid="ai-business-tabs">
        {["overview","catalog","projects","pricing","revenue","risks"].map(id=>(
          <button key={id} style={S.tabBtn(tab===id)} onClick={()=>setTab(id)}
            data-testid={`ai-tab-${id}`} type="button">{id.toUpperCase()}</button>
        ))}
      </nav>
      {err && <p style={S.err} data-testid="ai-business-error">{err}</p>}

      {tab==="overview" && overview && (
        <section style={S.grid} data-testid="ai-panel-overview">
          <article style={S.card}><h2 style={S.ct}>Delivery Mode</h2><p style={S.cs}>{overview.delivery_mode}</p><p style={S.cs}>{overview.positioning}</p></article>
          <article style={S.card}><h2 style={S.ct}>Catalog</h2><div style={{fontSize:26, color:C.gold}} data-testid="ai-catalog-count">{overview.catalog_size}</div><p style={S.cs}>Services ready to sell (with human review).</p></article>
          <article style={S.card}><h2 style={S.ct}>Active Projects</h2><div style={{fontSize:26, color:C.gold}}>{overview.active_projects}</div><p style={S.cs}>In intake / building / review / revision.</p></article>
          <article style={S.card}><h2 style={S.ct}>Human Review Queue</h2><div style={{fontSize:26, color:C.amber}}>{overview.human_review_queue}</div><p style={S.cs}>Projects awaiting human approval before delivery.</p></article>
        </section>
      )}

      {tab==="catalog" && (
        <section style={S.grid} data-testid="ai-panel-catalog">
          {catalog.map(s=>(
            <article key={s.service_id} style={S.card} data-testid={`ai-service-${s.service_id}`}>
              <h3 style={S.ct}>{s.name}</h3>
              <div style={S.kv}><span>Category</span><span>{s.category}</span></div>
              <div style={S.kv}><span>Suggested price</span><span>${s.suggested_price?.toLocaleString?.() ?? s.suggested_price}</span></div>
              <div style={S.kv}><span>Price floor</span><span>${s.price_floor?.toLocaleString?.() ?? s.price_floor}</span></div>
              <div style={S.kv}><span>SLA</span><span>{s.delivery_sla}</span></div>
              <div style={S.kv}><span>Human review</span><span style={{color:C.emerald}}>required</span></div>
            </article>
          ))}
        </section>
      )}

      {tab==="projects" && (
        <section style={S.grid} data-testid="ai-panel-projects">
          {projects.length===0 && <p style={S.cs}>No active projects yet.</p>}
          {projects.map(p=>(
            <article key={p.project_id} style={S.card}>
              <h3 style={S.ct}>{p.service_name || "Project"}</h3>
              <div style={S.kv}><span>Client</span><span>{p.client_name}</span></div>
              <div style={S.kv}><span>Status</span><span style={{color:C.gold}}>{p.status}</span></div>
              <div style={S.kv}><span>Review</span><span style={{color:p.human_review_status==="approved"?C.emerald:C.amber}}>{p.human_review_status}</span></div>
            </article>
          ))}
        </section>
      )}

      {tab==="pricing" && (
        <section style={{...S.grid, gridTemplateColumns:"repeat(auto-fit, minmax(260px, 1fr))"}} data-testid="ai-panel-pricing">
          <article style={S.card}>
            <h2 style={S.ct}>Inputs</h2>
            {[
              ["ai_tool_cost","AI tool / API cost ($)"],
              ["human_review_hours","Human review hours"],
              ["hourly_value","Hourly value ($/h)"],
            ].map(([k,label])=>(
              <label key={k} style={{...S.cs, display:"block", marginTop:8}} htmlFor={`inp-${k}`}>
                {label}
                <input id={`inp-${k}`} data-testid={`price-input-${k}`} type="number" style={S.input}
                       value={priceForm[k]} onChange={e=>setPriceForm({...priceForm, [k]: Number(e.target.value)})} />
              </label>
            ))}
            <label style={{...S.cs, display:"block", marginTop:8}}>
              Compliance risk
              <select style={S.input} value={priceForm.compliance_risk}
                data-testid="price-input-compliance_risk"
                onChange={e=>setPriceForm({...priceForm, compliance_risk:e.target.value})}>
                <option value="LOW">LOW</option><option value="MEDIUM">MEDIUM</option><option value="HIGH">HIGH</option>
              </select>
            </label>
            <button type="button" style={{...S.btn, marginTop:12}} onClick={calcPrice} data-testid="price-calc-btn">Calculate</button>
          </article>
          <article style={S.card} data-testid="ai-pricing-output">
            <h2 style={S.ct}>Recommended Pricing</h2>
            {!pricing && <p style={S.cs}>Enter inputs and press Calculate.</p>}
            {pricing && (
              <>
                <div style={{fontSize:26, color:C.gold, marginBottom:6}}>${pricing.recommended_price?.toLocaleString?.() ?? pricing.recommended_price}</div>
                <div style={S.kv}><span>Minimum profitable</span><span>${pricing.minimum_profitable_price?.toLocaleString?.()}</span></div>
                <div style={S.kv}><span>Total cost</span><span>${pricing.total_cost?.toLocaleString?.()}</span></div>
                <div style={S.kv}><span>Margin</span><span>${pricing.margin?.toLocaleString?.()} ({pricing.margin_percent}%)</span></div>
                <div style={S.kv}><span>Human review</span><span style={{color:C.emerald}}>{pricing.human_review_required?"required":"recommended"}</span></div>
                <div style={S.kv}><span>Truthfulness</span><span>{pricing.truthfulness_label}</span></div>
              </>
            )}
          </article>
        </section>
      )}

      {tab==="revenue" && rev && (
        <section style={S.grid} data-testid="ai-panel-revenue">
          <article style={S.card}><h2 style={S.ct}>Gross Revenue (delivered)</h2><div style={{fontSize:26, color:C.gold}} data-testid="ai-revenue-gross">${rev.gross_revenue?.toLocaleString?.() ?? rev.gross_revenue}</div><p style={S.cs}>{rev.note}</p></article>
          <article style={S.card}><h2 style={S.ct}>Gross Cost</h2><div style={{fontSize:26}}>${rev.gross_cost?.toLocaleString?.() ?? rev.gross_cost}</div></article>
          <article style={S.card}><h2 style={S.ct}>Estimated Margin</h2><div style={{fontSize:26, color:C.emerald}}>${rev.estimated_margin?.toLocaleString?.() ?? rev.estimated_margin}</div></article>
        </section>
      )}

      {tab==="risks" && (
        <section style={S.card} data-testid="ai-panel-risks">
          <h2 style={S.ct}>Risk & Compliance Guardrails</h2>
          <ul style={{color:C.ivoryD, fontSize:13, lineHeight:1.7, paddingLeft:18}}>
            <li>Never claim income guarantees or promise assured outcomes.</li>
            <li>Never automate regulated submissions without human approval.</li>
            <li>Never misrepresent AI-generated work as fully human.</li>
            <li>All client deliverables logged to EvidenceOS with immutable hash.</li>
            <li>Client data never used for model training unless explicitly authorized.</li>
          </ul>
        </section>
      )}

      <p style={S.disc} data-testid="ai-business-disclaimer">
        AI-enabled business services with human quality control. Not raw AI resale. No promises of returns.
        No autonomous legal, medical, or financial advice without proper disclaimers. EvidenceOS logs every delivery.
      </p>
    </main>
  );
}
